import { Check, Info, X } from "lucide-react";
import { useTranslate } from "ra-core";

import { canSeeAdvisorInvoiceNotice } from "../invoicing/payableScreenRules";
import type { AppRole } from "../types";

/**
 * QE6-A2: neutral notice (icon plus text, never colour alone, never an
 * amount) that the payment is generated only when the advisor uploads her own
 * invoice. Renders nothing for roles that can never be payee.
 */
export const AdvisorInvoiceNotice = ({ role }: { role: AppRole }) => {
  const translate = useTranslate();
  if (!canSeeAdvisorInvoiceNotice(role)) return null;
  return (
    <p
      data-testid="advisor-invoice-notice"
      className="flex items-start gap-1.5 text-sm text-muted-foreground"
    >
      <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <span>{translate("crm.invoicing.payables.advisor_invoice_notice")}</span>
    </p>
  );
};

const RequirementRow = ({ met, label }: { met: boolean; label: string }) => (
  <p className="flex items-center gap-1.5">
    {met ? (
      <Check className="size-4 shrink-0 text-brand-green" aria-hidden="true" />
    ) : (
      <X className="size-4 shrink-0 text-brand-red" aria-hidden="true" />
    )}
    <span>{label}</span>
  </p>
);

/**
 * Almikia Epic E6 (TASK-041): a small neutral hint of the two requirements of
 * the advisor's payable (green file, invoice uploaded). A check / cross icon
 * plus text (never colour alone), never an amount.
 */
export const PayableRequirementHint = ({
  requirements,
}: {
  requirements: { greenFile: boolean; invoiceUploaded: boolean };
}) => {
  const translate = useTranslate();
  return (
    <div
      data-testid="payable-requirements"
      className="space-y-0.5 text-sm text-muted-foreground"
    >
      <p className="font-medium">
        {translate("crm.operations.documents.payable_hint.title")}
      </p>
      <RequirementRow
        met={requirements.greenFile}
        label={translate(
          `crm.operations.documents.payable_hint.green_file_${requirements.greenFile ? "yes" : "no"}`,
        )}
      />
      <RequirementRow
        met={requirements.invoiceUploaded}
        label={translate(
          `crm.operations.documents.payable_hint.invoice_${requirements.invoiceUploaded ? "yes" : "no"}`,
        )}
      />
    </div>
  );
};
